import React, { useState, useImperativeHandle, forwardRef, useEffect, useRef } from 'react';
import Select from 'react-select';
import { useItemCategories, useItemSubcategories } from '../hooks/useQueries';
import { selectableTaskOrders } from '../utils/taskOrderUtils';
import './developIdeaForm.css';

// Hidden on this instance: category/subcategory are not selected when developing
// an idea, and are not required. They can still be set on the item afterwards.
const SHOW_DEVELOP_CATEGORIES = false;

const DevelopIdeaForm = forwardRef(({ item, onSave, onCancel, disabled = false, aiAnalysis = null, projects = [] }, ref) => {
  const [title, setTitle] = useState(item.title || item.description.substring(0, 50));
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [description, setDescription] = useState(item.description);
  const [initialNotes, setInitialNotes] = useState('');
  const [error, setError] = useState('');
  
  // AI suggestion states
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [categorySuggestionAccepted, setCategorySuggestionAccepted] = useState(false);
  const [subcategorySuggestionAccepted, setSubcategorySuggestionAccepted] = useState(false);
  
  // Refs to track when accept buttons are clicked to avoid race conditions
  const acceptingCategoryRef = useRef(false);
  const acceptingSubcategoryRef = useRef(false);

  // Custom option component for Select dropdowns
  const CustomOption = ({ children, ...props }) => {
    const { data } = props;
    return (
      <div
        {...props.innerProps}
        style={{
          padding: '8px 12px',
          cursor: 'pointer',
          backgroundColor: props.isFocused ? '#f8f9fa' : 'white',
          borderBottom: '1px solid #e9ecef'
        }}
      >
        <div style={{ fontWeight: '500', fontSize: '0.9rem', marginBottom: '2px' }}>
          {data.label}
        </div>
        {data.description && (
          <div style={{ 
            fontSize: '0.8rem', 
            color: '#6c757d', 
            fontStyle: 'italic',
            lineHeight: '1.2'
          }}>
            {data.description}
          </div>
        )}
      </div>
    );
  };

  // Custom single value component to show just the label in the selected value
  const CustomSingleValue = ({ children, ...props }) => {
    return (
      <div style={{ fontSize: '0.875rem', color: '#495057' }}>
        {props.data.label}
      </div>
    );
  };

  const { data: categories = [], isLoading: categoriesLoading } = useItemCategories();
  
  // Load subcategories for selected category OR AI suggested category
  const categoryIdForSubcategories = selectedCategory?.value || (aiAnalysis?.categoryId);
  const { data: subcategories = [], isLoading: subcategoriesLoading } = useItemSubcategories(categoryIdForSubcategories);

  const categoryOptions = categories.map(cat => ({ 
    value: cat.id, 
    label: cat.categoryname,
    description: cat.description
  }));
  const subcategoryOptions = subcategories.map(sub => ({ 
    value: sub.id, 
    label: sub.subcategoryname,
    description: sub.description
  }));
  // Creating a new item, so only task orders inside their active window are offered.
  const projectOptions = selectableTaskOrders(projects).map(project => ({
    value: project.id,
    label: project.name,
    description: `Owner: ${project.owner_name || 'Unassigned'}`
  }));

  // Setup AI suggestions when AI analysis is provided
  useEffect(() => {
    // Only reset suggestions when aiAnalysis actually changes, not when categories/subcategories load
    if (!aiAnalysis) {
      setAiSuggestions(null);
      setCategorySuggestionAccepted(false);
      setSubcategorySuggestionAccepted(false);
      return;
    }
    
    // Don't reset if aiSuggestions already exist for this analysis - preserve accepted states
    if (aiSuggestions && aiSuggestions.analysis === aiAnalysis.analysis) {
      // Just update subcategory if it becomes available
      if (aiAnalysis.subcategoryId && subcategories.length > 0 && !aiSuggestions.subcategory) {
        const aiSubcategory = subcategories.find(sub => sub.id === aiAnalysis.subcategoryId);
        if (aiSubcategory) {
          setAiSuggestions(prev => ({
            ...prev,
            subcategory: {
              value: aiSubcategory.id,
              label: aiSubcategory.subcategoryname,
              description: aiSubcategory.description
            }
          }));
        }
      }
      return;
    }
    
    // First time setting up suggestions for this analysis
    if (aiAnalysis.categoryId && categories.length > 0) {
      // Find the suggested category
      const aiCategory = categories.find(cat => cat.id === aiAnalysis.categoryId);
      
      if (aiCategory) {
        // Find the suggested subcategory if available
        let aiSubcategory = null;
        if (aiAnalysis.subcategoryId && subcategories.length > 0) {
          aiSubcategory = subcategories.find(sub => sub.id === aiAnalysis.subcategoryId);
        }

        // Set up suggestions with category and subcategory info
        setAiSuggestions({
          category: {
            value: aiCategory.id,
            label: aiCategory.categoryname,
            description: aiCategory.description
          },
          subcategory: aiSubcategory ? {
            value: aiSubcategory.id,
            label: aiSubcategory.subcategoryname,
            description: aiSubcategory.description
          } : null,
          classification: aiAnalysis.classification,
          analysis: aiAnalysis.analysis
        });
        
        // Reset accepted states only when setting up new suggestions
        setCategorySuggestionAccepted(false);
        setSubcategorySuggestionAccepted(false);
      }
    }
  }, [aiAnalysis, categories, subcategories, aiSuggestions]);
  
  // Handle accepting category suggestion
  const handleAcceptCategorySuggestion = () => {
    if (aiSuggestions && aiSuggestions.category) {
      acceptingCategoryRef.current = true;
      setSelectedCategory(aiSuggestions.category);
      setCategorySuggestionAccepted(true);
      // Clear subcategory selection since we're changing category
      setSelectedSubcategory(null);
      setSubcategorySuggestionAccepted(false);
    }
  };
  
  // Handle accepting subcategory suggestion
  const handleAcceptSubcategorySuggestion = () => {
    if (aiSuggestions && aiSuggestions.subcategory) {
      acceptingSubcategoryRef.current = true;
      setSelectedSubcategory(aiSuggestions.subcategory);
      setSubcategorySuggestionAccepted(true);
    }
  };

  // Handle manual category selection (reset AI suggestion states)
  const handleCategoryChange = (selectedOption) => {
    setSelectedCategory(selectedOption);
    setSelectedSubcategory(null); // Clear subcategory when category changes
    
    // If this was triggered by accept button, don't override the accepted state
    if (acceptingCategoryRef.current) {
      acceptingCategoryRef.current = false;
      return;
    }
    
    // Check if the selected category matches the AI suggestion
    if (selectedOption && aiSuggestions && aiSuggestions.category && 
        selectedOption.value === aiSuggestions.category.value) {
      // User selected the same category AI suggested - treat as accepted
      setCategorySuggestionAccepted(true);
    } else {
      // User selected different category - reset AI suggestion states
      setCategorySuggestionAccepted(false);
    }
    
    // Always reset subcategory suggestion state when category changes
    setSubcategorySuggestionAccepted(false);
  };

  // Handle manual subcategory selection
  const handleSubcategoryChange = (selectedOption) => {
    setSelectedSubcategory(selectedOption);
    
    // If this was triggered by accept button, don't override the accepted state
    if (acceptingSubcategoryRef.current) {
      acceptingSubcategoryRef.current = false;
      return;
    }
    
    // Check if the selected subcategory matches the AI suggestion
    if (selectedOption && aiSuggestions && aiSuggestions.subcategory && 
        selectedOption.value === aiSuggestions.subcategory.value) {
      // User selected the same subcategory AI suggested - treat as accepted
      setSubcategorySuggestionAccepted(true);
    } else {
      // User selected different subcategory - reset AI suggestion state
      setSubcategorySuggestionAccepted(false);
    }
  };

  // Category/subcategory are only required where they are shown; the develop
  // endpoint already accepts them as null.
  const hasRequiredFields = () => Boolean(
    title.trim()
    && selectedProject
    && (!SHOW_DEVELOP_CATEGORIES || (selectedCategory && selectedSubcategory))
  );

  const validateAndSubmit = () => {
    if (!hasRequiredFields()) {
      setError('Please fill in all required fields.');
      return false;
    }
    setError('');
    onSave({
      brainstormingId: item.id,
      title,
      categoryId: selectedCategory?.value ?? null,
      subcategoryId: selectedSubcategory?.value ?? null,
      projectId: selectedProject.value,
      description,
      initialNotes,
    });
    return true;
  };

  // Expose the submit function and validation state to parent via ref
  useImperativeHandle(ref, () => ({
    submit: validateAndSubmit,
    isValid: hasRequiredFields,
    getCurrentValues: () => ({
      title: title.trim(),
      description: description.trim()
    })
  }));

  const handleSubmit = (e) => {
    e.preventDefault();
    validateAndSubmit();
  };

  return (
    <form onSubmit={handleSubmit} className="develop-idea-form">
      {error && <div style={{ color: 'red', marginBottom: '8px' }}>{error}</div>}
      
      <div className="form-group">
        <label htmlFor="title">Title <span style={{color: 'red'}}>*</span></label>
        <input
          id="title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={disabled}
          required
        />
      </div>
      {SHOW_DEVELOP_CATEGORIES && (
      <>
      <div className="form-group">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <label htmlFor="category">Category <span style={{color: 'red'}}>*</span></label>
          {aiSuggestions && aiSuggestions.category && !categorySuggestionAccepted && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', color: '#4338ca', fontWeight: '500' }}>
                AI suggests: {aiSuggestions.category.label}
              </span>
              <button
                type="button"
                onClick={handleAcceptCategorySuggestion}
                style={{
                  backgroundColor: '#16a34a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '11px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Accept
              </button>
            </div>
          )}
          {categorySuggestionAccepted && (
            <span style={{ fontSize: '12px', color: '#16a34a', fontWeight: '500' }}>✓ AI suggestion accepted</span>
          )}
        </div>
        <Select
          id="category"
          options={categoryOptions}
          isLoading={categoriesLoading}
          onChange={handleCategoryChange}
          value={selectedCategory}
          placeholder="Select a category..."
          isClearable={true}
          isDisabled={disabled}
          components={{
            Option: CustomOption,
            SingleValue: CustomSingleValue
          }}
          styles={{
            menuPortal: base => ({ ...base, zIndex: 9999 }),
            menu: (provided) => ({
              ...provided,
              zIndex: 9999,
            })
          }}
          menuPortalTarget={document.body}
        />
      </div>
      <div className="form-group">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <label htmlFor="subcategory">Subcategory <span style={{color: 'red'}}>*</span></label>
          {aiSuggestions && aiSuggestions.subcategory && !subcategorySuggestionAccepted && categorySuggestionAccepted && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', color: '#4338ca', fontWeight: '500' }}>
                AI suggests: {aiSuggestions.subcategory.label}
              </span>
              <button
                type="button"
                onClick={handleAcceptSubcategorySuggestion}
                style={{
                  backgroundColor: '#16a34a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '11px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Accept
              </button>
            </div>
          )}
          {aiSuggestions && !categorySuggestionAccepted && (
            <span style={{ fontSize: '12px', color: '#9ca3af' }}>Accept category suggestion first</span>
          )}
          {aiSuggestions && !aiSuggestions.subcategory && categorySuggestionAccepted && (
            <span style={{ fontSize: '12px', color: '#9ca3af' }}>Loading suggestions...</span>
          )}
          {subcategorySuggestionAccepted && (
            <span style={{ fontSize: '12px', color: '#16a34a', fontWeight: '500' }}>✓ AI suggestion accepted</span>
          )}
        </div>
        <Select
          id="subcategory"
          options={subcategoryOptions}
          isLoading={subcategoriesLoading}
          onChange={handleSubcategoryChange}
          value={selectedSubcategory}
          isDisabled={!selectedCategory || subcategoriesLoading || disabled}
          placeholder={selectedCategory ? "Select a subcategory..." : "Select a category first"}
          isClearable={true}
          components={{
            Option: CustomOption,
            SingleValue: CustomSingleValue
          }}
          styles={{
            menuPortal: base => ({ ...base, zIndex: 9999 }),
            menu: (provided) => ({
              ...provided,
              zIndex: 9999,
            })
          }}
          menuPortalTarget={document.body}
        />
      </div>
      </>
      )}
      <div className="form-group">
        <label htmlFor="project">Task Order <span style={{color: 'red'}}>*</span></label>
        <Select
          id="project"
          options={projectOptions}
          onChange={setSelectedProject}
          value={selectedProject}
          placeholder="Select a task order..."
          isClearable={true}
          isDisabled={disabled}
          components={{
            Option: CustomOption,
            SingleValue: CustomSingleValue
          }}
          styles={{
            menuPortal: base => ({ ...base, zIndex: 9999 }),
            menu: (provided) => ({
              ...provided,
              zIndex: 9999,
            })
          }}
          menuPortalTarget={document.body}
        />
      </div>
      <div className="form-group">
        <label htmlFor="description">Description</label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows="4"
          disabled={disabled}
          placeholder="Describe the idea in detail..."
        />
      </div>
      <div className="form-group">
        <label htmlFor="initial-notes">Initial Notes</label>
        <textarea
          id="initial-notes"
          value={initialNotes}
          onChange={(e) => setInitialNotes(e.target.value)}
          rows="4"
          disabled={disabled}
          placeholder="Add any initial notes or context..."
        />
      </div>
    </form>
  );
});

export default DevelopIdeaForm;
